import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Drawer,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { legalDocuments } from "../../../../shared/data/legalDocuments.js";
import i18n from "../../../../utils/i18n.js";
import classes from "./continueLaterPanel.module.scss";

const EMAIL_EXAMPLE = "bela@pelda.com";
const VALID_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const isValidEmail = (value) => VALID_EMAIL_REGEX.test((value || "").trim());

const submitButtonSx = {
  textTransform: "none",
  minHeight: 52,
  borderRadius: "12px",
  fontFamily: "Satoshi, sans-serif",
  fontSize: 16,
  fontWeight: 700,
  backgroundColor: "#1c32f3",
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#1528c4",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e2e8f0",
    color: "#64748b",
  },
};

const dialogPaperSx = {
  width: "calc(100% - 48px)",
  maxWidth: 520,
  margin: "32px auto",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(2, 6, 23, 0.12)",
  overflow: "hidden",
};

const ContinueLaterPanel = ({ open, onClose }) => {
  const isMobileLayout = useMediaQuery("(max-width:599.98px)");
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  const emailValid = useMemo(() => isValidEmail(email), [email]);
  const showEmailError =
    !emailFocused &&
    (emailTouched || email.trim().length > 0) &&
    !emailValid;

  useEffect(() => {
    if (!open) {
      setEmail("");
      setEmailTouched(false);
      setEmailFocused(false);
    }
  }, [open]);

  const handleEmailChange = useCallback((event) => {
    setEmail(event.target.value);
  }, []);

  const handleEmailFocus = useCallback(() => {
    setEmailFocused(true);
  }, []);

  const handleEmailBlur = useCallback(() => {
    setEmailFocused(false);
    setEmailTouched(true);
  }, []);

  const panelBody = (
    <Box className={classes.content}>
      {isMobileLayout && <div className={classes.handle} aria-hidden="true" />}
      <Box className={classes.scrollBody}>
          <h2 id="continue-later-title" className={classes.title}>
            {i18n.t("searchflow.savelaterheading")}
          </h2>

          <p className={classes.subtitle}>
            {i18n.t("searchflow.savelatersubtitle")}
          </p>

          <div className={classes.emailFieldWrap}>
            <label className={classes.emailLabel} htmlFor="continue-later-email">
              {i18n.t("searchflow.savelateremaillabel")}
            </label>
            <TextField
              id="continue-later-email"
              fullWidth
              type="email"
              placeholder={EMAIL_EXAMPLE}
              value={email}
              onChange={handleEmailChange}
              onFocus={handleEmailFocus}
              onBlur={handleEmailBlur}
              error={showEmailError}
              className={classes.emailField}
              sx={{
                "& .MuiInputBase-input": { fontFamily: "Satoshi" },
                "& .MuiInputBase-input::placeholder": { fontFamily: "Satoshi" },
              }}
            />
          </div>
          {showEmailError && (
            <Typography className={classes.emailError} role="alert" aria-live="polite">
              {i18n.t("searchflow.invalidemailhint")} {EMAIL_EXAMPLE}
            </Typography>
          )}

          <label className={classes.marketingOption} htmlFor="continue-later-marketing">
            <Checkbox              id="continue-later-marketing"
              name="marketing"
              defaultChecked={false}
              disableRipple
              className={classes.marketingCheckbox}
              sx={{
                padding: 0,
                color: "#94a3b8",
                "&.Mui-checked": {
                  color: "#1c32f3",
                },
              }}
            />
            <span className={classes.marketingLabelText}>
              {i18n.t("searchflow.continuelaternewsletter")}{" "}
              <span className={classes.marketingOptional}>
                {i18n.t("searchflow.optionaltag")}
              </span>
            </span>
          </label>
        </Box>

        <Box className={classes.footer}>
          <p className={classes.legalText}>
            {i18n.t("searchflow.legalprefix")}{" "}
            <a
              className={classes.legalLink}
              rel="noopener noreferrer"
              target="_blank"
              href={legalDocuments.gdpr}
            >
              {i18n.t("gdpr.part2")}
            </a>{" "}
            {i18n.t("gdpr.part3")}{" "}
            <a
              className={classes.legalLink}
              rel="noopener noreferrer"
              target="_blank"
              href={legalDocuments.aszf}
            >
              {i18n.t("gdpr.part4")}
            </a>
            {i18n.t("gdpr.part5")}
          </p>
          <Button
            type="button"
            variant="contained"
            fullWidth
            disabled={!emailValid}
            endIcon={
              <ChevronRightIcon
                sx={{ color: emailValid ? "#fff" : "#64748b" }}
              />
            }
            sx={submitButtonSx}
          >
            {i18n.t("searchflow.sfresbuttontext2")}
          </Button>
        </Box>
    </Box>
  );

  if (isMobileLayout) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{ className: classes.drawerPaper }}
      >
        {panelBody}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="continue-later-title"
      maxWidth={false}
      PaperProps={{ sx: dialogPaperSx }}
    >
      {panelBody}
    </Dialog>
  );
};

export default ContinueLaterPanel;
